import type { ChatSearchHit, ChatSearchQuery, FilesFilter, Snippet } from "@beacon/core/chat-search"
import { describeRelativeTime } from "@/time/describe.ts"
import { placeOf } from "./describe.ts"
import { messageHref } from "./message-place.ts"
import { authorWords } from "./authors.ts"

/*
 * Chat search's rules, apart from its page so they can be checked without a browser: the search as
 * the address keeps it (`/chat/search?q=…&in=…&from=…&date=…&files=…`), so a link, a reload and
 * Back all keep it; what it asks the web API; and how a result is shown and named.
 */

/** How far back a search's Date filter reaches: since today began, or the past week, month or year. */
export const datePresets = ["today", "week", "month", "year"] as const
export type DatePreset = (typeof datePresets)[number]

/** The Date filter's choices, in words. */
export const dateLabels: Record<DatePreset, string> = {
  today: "Today",
  week: "Past 7 days",
  month: "Past 30 days",
  year: "Past 12 months",
}

/** The Files filter's choices, in words. */
export const filesLabels: Record<FilesFilter, string> = {
  any: "Has files",
  images: "Has images",
  documents: "Has other files",
}
const filesFilters = Object.keys(filesLabels) as FilesFilter[]

/**
 * A search as its page holds it: its words, the Conversations (`in`) and authors (`from`) it keeps
 * to, any of each, how far back it reaches and what files its messages carry.
 */
export type SearchView = {
  text: string
  in: string[]
  from: string[]
  date: DatePreset | null
  files: FilesFilter | null
}

const one = <T extends string>(value: string | null, among: readonly T[]) =>
  among.includes(value as T) ? (value as T) : null

/** The search an address keeps. A date or files value it doesn't know is no filter. */
export function searchViewOf(params: URLSearchParams): SearchView {
  return {
    text: params.get("q") ?? "",
    in: params.getAll("in"),
    from: params.getAll("from"),
    date: one(params.get("date"), datePresets),
    files: one(params.get("files"), filesFilters),
  }
}

/**
 * The address's parameters with `changes` made to the search it keeps, each part left out once
 * empty, and the address's other parameters as they were.
 */
export function withSearchView(params: URLSearchParams, changes: Partial<SearchView>) {
  const next = new URLSearchParams(params)
  const single = (name: string, value: string | null | undefined) => {
    if (value === undefined) return
    next.delete(name)
    if (value) next.set(name, value)
  }
  const many = (name: string, values: string[] | undefined) => {
    if (values === undefined) return
    next.delete(name)
    for (const value of values) next.append(name, value)
  }
  single("q", changes.text)
  many("in", changes.in)
  many("from", changes.from)
  single("date", changes.date)
  single("files", changes.files)
  return next
}

/** The search page's address, searching as `view` says: Search in this conversation's link. */
export function searchHref(view: Partial<SearchView>) {
  const query = withSearchView(new URLSearchParams(), view).toString()
  return query ? `/chat/search?${query}` : "/chat/search"
}

/** Whether the words hold a word: a letter or a digit, as the web API counts them. */
const holdsWord = (text: string) => /[\p{L}\p{N}]/u.test(text)

/** Whether there is anything to search for yet: a word, or a filter. */
export function isAsked(view: SearchView) {
  return (
    holdsWord(view.text) ||
    view.in.length > 0 ||
    view.from.length > 0 ||
    view.date !== null ||
    view.files !== null
  )
}

/** The moment a Date filter reaches back to, on the Member's own calendar. */
function startOf(preset: DatePreset, now: Date) {
  const [year, month, day] = [now.getFullYear(), now.getMonth(), now.getDate()]
  switch (preset) {
    case "today":
      return new Date(year, month, day)
    case "week":
      return new Date(year, month, day - 7)
    case "month":
      return new Date(year, month - 1, day)
    case "year":
      return new Date(year - 1, month, day)
  }
}

/** What a search asks the web API, its Date filter read as of `now`. */
export function queryOf(view: SearchView, now: Date): ChatSearchQuery {
  const text = view.text.trim()
  return {
    ...(text && { text }),
    ...(view.in.length > 0 && { conversationIds: view.in }),
    ...(view.from.length > 0 && { authorIds: view.from }),
    ...(view.date && { after: startOf(view.date, now).toISOString() }),
    ...(view.files && { files: view.files }),
  }
}

/** The web API's address for a page of a search: `cursor` the one the last page gave. */
export function searchPath(query: ChatSearchQuery, cursor?: string) {
  const params = new URLSearchParams()
  if (query.text) params.set("q", query.text)
  for (const id of query.conversationIds ?? []) params.append("in", id)
  for (const id of query.authorIds ?? []) params.append("from", id)
  if (query.after) params.set("after", query.after)
  if (query.before) params.set("before", query.before)
  if (query.files) params.set("files", query.files)
  if (cursor) params.set("cursor", cursor)
  return `/chat/search?${params.toString()}`
}

/**
 * Where a result opens: its message in its conversation, or in its thread beside it for a reply,
 * the message itself landed on (the message's own address, `messageHref`).
 */
export const hitHref = ({ conversationId, messageId, threadRootId }: ChatSearchHit) =>
  messageHref({ conversationId, id: messageId, threadRootId })

/** A snippet as runs of text, each marked where it is a word searched for. */
export function segmentsOf({ text, marks }: Snippet) {
  const segments: { text: string; marked: boolean }[] = []
  let at = 0
  for (const [from, to] of marks) {
    if (from > at) segments.push({ text: text.slice(at, from), marked: false })
    segments.push({ text: text.slice(from, to), marked: true })
    at = to
  }
  if (at < text.length) segments.push({ text: text.slice(at), marked: false })
  return segments
}

/**
 * A result as a screen reader hears its link: who said it where and when, then what it says (its
 * files' names, for one that says nothing else).
 */
export function describeHit(hit: ChatSearchHit, now: Date = new Date()) {
  const where = `${hit.threadRootId ? "a thread in " : ""}${placeOf(hit.place)}`
  const said = hit.snippet.text || hit.files.map(({ name }) => name).join(", ")
  return `${authorWords(hit.author)} in ${where}, ${describeRelativeTime(hit.sentAt, now)}: ${said}`
}

/** How many results show: "No messages", "1 message", "20+ messages" while more can be read. */
export function describeFound(count: number, more: boolean) {
  if (count === 0) return "No messages"
  return `${count}${more ? "+" : ""} ${count === 1 && !more ? "message" : "messages"}`
}
